/* Riquadro per creare subito su Discord un canale (anche in una categoria nuova) o una categoria, anche privati. */
import { useState } from "react";
import { toast } from "sonner";
import { FolderPlus, Loader2, Plus } from "lucide-react";
import { apiError, botPost } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Input } from "@/components/ui/input";
import { FancyNameInput } from "@/components/FancyNameInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputC = "border-[#232838] bg-[#131620] text-white placeholder:text-[#5f6479] focus-visible:ring-[#5865F2]";

export const slugChannel = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[#@:`]/g, "").replace(/-{2,}/g, "-").slice(0, 100);

/** Riquadro per creare subito su Discord un canale (anche dentro una categoria nuova) o una categoria. */
export function CreateChannelBox({ isCategory, suggestName, suggestParentId, onCancel, onCreated, privateDefault }) {
  const { channels, refresh } = useData();
  const categories = channels.filter((c) => c.type === "category");
  const [name, setName] = useState(isCategory ? (suggestName || "").toUpperCase() : slugChannel(suggestName));
  const [parent, setParent] = useState(suggestParentId && categories.some((c) => c.id === suggestParentId) ? suggestParentId : "__none__");
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  // Canali di log/staff: di solito devono essere privati (@everyone non li vede).
  const [priv, setPriv] = useState(privateDefault ?? /log|staff|mod|admin/i.test(suggestName || ""));
  const create = async () => {
    const clean = isCategory ? name.trim() : slugChannel(name);
    if (!clean) return toast.error(isCategory ? "Scrivi il nome della categoria" : "Scrivi il nome del canale");
    if (!isCategory && parent === "__new__" && !newCat.trim()) return toast.error("Scrivi il nome della nuova categoria");
    setBusy(true);
    try {
      let parentId = parent === "__none__" ? null : parent;
      if (!isCategory && parent === "__new__") parentId = (await botPost("channels", { name: newCat.trim(), type: "category" })).id;
      const hidden = priv ? { permissionOverwrites: [{ id: "everyone", type: "role", deny: ["ViewChannel"] }] } : {};
      const ch = await botPost("channels", isCategory ? { name: clean, type: "category", ...hidden } : { name: clean, type: "text", parentId, ...hidden });
      await refresh();
      toast.success(isCategory ? `Categoria «${ch.name}» creata su Discord` : `Canale #${ch.name} creato su Discord`);
      onCreated(ch.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2.5 rounded-xl border border-[#5865F2]/40 bg-[#5865F2]/[0.07] p-3">
      <p className="flex items-center gap-1.5 text-xs font-bold text-white">
        {isCategory ? <FolderPlus className="h-3.5 w-3.5 text-[#8ea0ff]" /> : <Plus className="h-3.5 w-3.5 text-[#8ea0ff]" />}
        {isCategory ? "Nuova categoria su Discord" : "Nuovo canale testuale su Discord"}
      </p>
      <div className="flex items-center gap-1">
        {!isCategory && <span className="text-[#949BA4]">#</span>}
        <div className="min-w-0 flex-1">
          <FancyNameInput value={name} onChange={(v) => setName(isCategory ? v.slice(0, 100) : v.toLowerCase().replace(/\s+/g, "-").slice(0, 100))}
            placeholder={isCategory ? "Es. TICKET SHOP" : "es. apri-ticket"} className={`${inputC} h-9`} />
        </div>
      </div>
      {!isCategory && (
        <>
          <Select value={parent} onValueChange={setParent}>
            <SelectTrigger className={`${inputC} h-9`}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72 border-[#232838] bg-[#161923] text-[#DBDEE1]">
              <SelectItem value="__none__" className="focus:bg-[#252B3D]"><span className="text-[#949BA4]">Fuori dalle categorie</span></SelectItem>
              <SelectItem value="__new__" className="focus:bg-[#252B3D]"><span className="font-semibold text-[#8ea0ff]">➕ In una nuova categoria…</span></SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id} className="focus:bg-[#252B3D]">📁 {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {parent === "__new__" && <Input value={newCat} onChange={(e) => setNewCat(e.target.value.slice(0, 100))} placeholder="Nome della nuova categoria" className={`${inputC} h-9`} />}
        </>
      )}
      <label className="flex cursor-pointer items-center gap-2 text-xs text-[#DBDEE1]">
        <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} className="h-4 w-4 accent-[#5865F2]" />
        🔒 Visibile solo allo staff <span className="text-[#6b7185]">(i membri normali non lo vedono; amministratori e bot sì)</span>
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#B5BAC1] hover:bg-white/5">Annulla</button>
        <button type="button" onClick={create} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-[#5865F2] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#4752c4] disabled:opacity-60">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Crea su Discord
        </button>
      </div>
    </div>
  );
}

